/**
 * O documento da proposta — a folha em si.
 *
 * Vive separado da página porque duas telas o imprimem: a interna, que o comercial confere, e o
 * link público, que o cliente abre. Uma cópia em cada lugar divergiria no dia em que alguém
 * mexesse numa e esquecesse a outra — e o que o cliente vê tem de ser o que o comercial viu.
 *
 * O visual é o da proposta que o comercial já usava (o PDF do Design): navy, o azul de destaque,
 * seções numeradas, cards e os dois cartões de preço. O que muda é que aqui os números não são
 * exemplo — saem das vidas e dos valores gravados.
 *
 * As cores são literais e sempre claras, sem passar pelo tema. Isto é papel: um documento que
 * muda de cor conforme o modo escuro de quem abriu não é um documento, e o cliente recebe o PDF.
 */

import React from 'react';
import { Box } from '@mui/material';
import branding from '../../../config/branding';
import { emReais, type PropostaPublica } from '../../../types/proposal';

/** A paleta do documento. Amostrada da proposta do Design, para o impresso bater com ela. */
const COR = {
  navy: '#152B45',
  navyClaro: '#1E4C7A',
  destaque: '#1B9CD8',
  ciano: '#2BB0E8',
  tinta: '#16202C',
  texto: '#364152',
  suave: '#7D8899',
  superficie: '#F5F8FA',
  borda: '#E4EAF0',
  totalFundo: '#E7F2FB',
} as const;

const FAMILIA =
  "'Figtree', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

function dataBR(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

function hojeBR(): string {
  return new Date().toLocaleDateString('pt-BR');
}

/**
 * CNPJ com máscara. O do prospecto chega como a pessoa digitou; o do emissor vem do cadastro,
 * onde é guardado só com dígitos — sem isto, o cabeçalho sai com um número corrido.
 */
function cnpjBR(valor: string | null | undefined): string {
  const digitos = (valor ?? '').replace(/\D/g, '');
  if (digitos.length !== 14) return valor ?? '—';
  return digitos.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

/** Números de seção com dois dígitos, como no original: 01, 02, 03. */
function doisDigitos(n: number): string {
  return String(n).padStart(2, '0');
}

// --- o conteúdo fixo, na ordem em que o cliente lê ---

const O_QUE_PASSA_A_TER: readonly { titulo: string; itens: readonly string[] }[] = [
  {
    titulo: 'Gestão completa',
    itens: [
      'Cadastro de empresa, unidades, setores, cargos e funcionários',
      'Inventário de riscos e plano de ação (PGR / NR-1)',
      'Programa de controle médico (PCMSO)',
      'Riscos psicossociais da NR-1, com avaliação digital',
      'Prontuário ocupacional e histórico por trabalhador',
    ],
  },
  {
    titulo: 'Agendamento',
    itens: [
      'Agenda de exames por clínica, com horários e escalas',
      'Sua empresa solicita e a clínica aceita, recusa ou reagenda',
      'Convocação automática do trabalhador',
      'Recepção e check-in no dia',
      'Controle de comparecimento e falta',
    ],
  },
  {
    titulo: 'Avisos de vencimento',
    itens: [
      'Exames: dispara a convocação antes de vencer',
      'EPI e EPC, pela validade do C.A.',
      'Treinamentos, pela periodicidade de cada NR',
      'Equipamentos de medição, com calibração',
      'Certificado digital do eSocial',
    ],
  },
  {
    titulo: 'Documentos e eSocial',
    itens: [
      'PGR, PCMSO, LTCAT, PPP, PCA, PPR, PCMAT, AET e outros',
      'Eventos S-2210, S-2220, S-2240 e S-2221 transmitidos',
      'CAT: comunicação de acidente',
      'CIPA, com processo eleitoral e votação on-line',
      'Assinatura eletrônica com validade legal',
    ],
  },
];

const DO_CLUBE: readonly string[] = [
  'Acompanhamento contínuo da saúde mental do time',
  'Avaliação por instrumentos validados, aplicada digitalmente',
  'Campanhas com prazo, convite por link e resposta do trabalhador',
  'Resultado que alimenta o inventário de riscos psicossociais da NR-1',
  'Leitura por setor e por cargo, sem expor a resposta individual',
];

/** O que sustenta a confiança quando a proposta é só o Clube: sigilo e método, não eSocial. */
const POR_QUE_CONFIAR_CLUBE: readonly { titulo: string; texto: string }[] = [
  {
    titulo: 'Instrumento validado, não questionário caseiro',
    texto:
      'A avaliação usa instrumento reconhecido na literatura, aplicado digitalmente. É o que sustenta o resultado quando ele precisar ser apresentado a um auditor ou a um sindicato.',
  },
  {
    titulo: 'A resposta individual não é exposta',
    texto:
      'O colaborador responde em sigilo. A leitura sai por setor e por cargo, com corte mínimo de respondentes — ninguém consegue chegar a quem disse o quê.',
  },
  {
    titulo: 'O resultado vira inventário, não relatório parado',
    texto:
      'O que a campanha mede alimenta o inventário de riscos psicossociais exigido pela NR-1, no formato em que ele é cobrado.',
  },
  {
    titulo: 'Dado de saúde tratado como dado de saúde',
    texto:
      'Isolamento entre empresas verificado a cada versão, credenciais guardadas criptografadas e registro de quem acessou o quê.',
  },
];

/** Os passos quando a proposta é só o Clube: não há cadastro de SST a importar. */
const COMO_COMECA_CLUBE: readonly string[] = [
  'Aceite da proposta e assinatura do contrato.',
  'Envio da relação de colaboradores que vão responder — nome e um contato para o convite.',
  'Configuração da primeira campanha, com o instrumento e o prazo de resposta.',
  'Convites disparados por WhatsApp ou e-mail, com resposta individual sigilosa.',
  'Leitura do resultado por setor e por cargo, e alimentação do inventário de riscos.',
];

const POR_QUE_CONFIAR: readonly { titulo: string; texto: string }[] = [
  {
    titulo: 'Conformidade que não depende de lembrete',
    texto:
      'Os quatro eventos de SST do eSocial são transmitidos, e a emissão de documento recusa quando o laudo sairia errado: sem responsável técnico habilitado, sem avaliação no quadro. É a diferença entre gerar PDF e responder pelo que se assina.',
  },
  {
    titulo: 'O trabalhador tem acesso ao que é dele',
    texto:
      'Portal próprio, sem senha para criar ou esquecer: ele informa o CPF, recebe um link e vê os próprios exames, treinamentos e atestados. Reduz a fila na porta do RH.',
  },
  {
    titulo: 'Dado de saúde tratado como dado de saúde',
    texto:
      'Isolamento entre empresas verificado a cada versão, senha e credencial guardadas criptografadas, e registro de quem acessou o quê. Prontuário tem guarda legal, e o sistema é construído para ela.',
  },
  {
    titulo: 'Migração sem perder o cadastro',
    texto:
      'Funcionários, setores, cargos, EPI, EPC e riscos sobem por planilha, com conferência antes de gravar. Histórico clínico de outro fornecedor é tratado como projeto à parte, com prazo combinado.',
  },
];

const COMO_COMECA: readonly string[] = [
  'Aceite da proposta e assinatura do contrato.',
  'Importação do cadastro: sua empresa envia a planilha de funcionários e nós subimos, com conferência antes de gravar.',
  'Configuração dos riscos e do PCMSO, junto com o responsável técnico.',
  'Agenda ligada e primeiras convocações disparadas.',
  'Acompanhamento: os avisos passam a rodar sozinhos.',
];


export interface DocumentoDaPropostaProps {
  proposta: PropostaPublica;
}

export const DocumentoDaProposta: React.FC<DocumentoDaPropostaProps> = ({ proposta }) => {
  const t = proposta.totais;
  // Ausente é verdadeiro: proposta gravada antes do campo existir tem gestão.
  const comGestao = proposta.managementEnabled !== false;
  const modulos = proposta.modules ?? [];
  // A casa é sempre a mesma, independentemente de a proposta ter saído de uma clínica ou da
  // própria plataforma: quem assina o contrato de gestão é a Lidera, e é a marca dela que o
  // cliente precisa reconhecer no papel. A clínica que emitiu continua gravada — ela é o recorte
  // de acesso —, mas não é o que se imprime.
  const aCasa = branding.appName;

  /**
   * A numeração é atribuída na hora, e não escrita à mão: o Clube e a seção de exclusão só
   * existem em algumas propostas, e número fixo deixaria buraco na sequência quando elas somem.
   */
  let contador = 0;
  const proximo = (): string => doisDigitos(++contador);

  return (
    <>
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Figtree:wght@400;500;600;700;800&display=swap"
      />

      <style>{`
        /*
         * O rodape sai uma vez, no fim, e nao repetido em toda pagina: repetir exige um elemento
         * fixo, e elemento fixo na impressao do Chrome nao reserva espaco -- ele cai por cima da
         * ultima linha de cada folha. Num documento que vai para o cliente isso nao passa. A
         * numeracao de pagina o proprio dialogo de impressao coloca.
         */
        @media print {
          @page { size: A4; margin: 14mm 0 16mm; }
          header, nav, aside, .MuiDrawer-root, .MuiAppBar-root, .nao-imprime { display: none !important; }
          body, #root { background: #fff !important; }
          .folha { box-shadow: none !important; margin: 0 !important; width: auto !important;
                   max-width: none !important; padding: 0 14mm !important; }
          .bloco { break-inside: avoid; page-break-inside: avoid; }
        }
        /* :where() zera a especificidade. Sem isso, ".doc p" (0,1,1) ganha das classes do MUI
           (0,1,0) e come todo espacamento que os componentes declaram em sx. */
        :where(.doc) :where(h1, h2, h3, p, ul, ol, li) { margin: 0; }
        :where(.doc) :where(ul) { padding-left: 1.15em; }
        :where(.doc) :where(li) { margin-bottom: 6px; }
      `}</style>

      <Box
        className="folha doc"
        sx={{
          maxWidth: 860,
          mx: 'auto',
          px: { xs: 3, md: 7 },
          py: { xs: 4, md: 7 },
          bgcolor: '#fff',
          boxShadow: '0 2px 24px rgba(21,43,69,.10)',
          color: COR.texto,
          fontSize: 14,
          lineHeight: 1.62,
        }}
      >
        {/* --- cabeçalho --- */}
        <Box
          className="bloco"
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3 }}
        >
          <Marca />
          <Box sx={{ textAlign: 'right', fontSize: 12.5, lineHeight: 1.9 }}>
            <DadoDoTopo rotulo="Proposta nº" valor={proposta.number} />
            <DadoDoTopo rotulo="Data" valor={hojeBR()} />
            <DadoDoTopo rotulo="Validade" valor={dataBR(proposta.validUntil)} />
          </Box>
        </Box>

        {/* --- abertura --- */}
        <Box className="bloco" sx={{ mt: 6 }}>
          <Box
            sx={{
              color: COR.destaque,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '.22em',
              textTransform: 'uppercase',
            }}
          >
            Proposta comercial
          </Box>
          <Box
            component="h1"
            sx={{
              mt: 1.5,
              fontSize: { xs: 32, md: 42 },
              lineHeight: 1.12,
              fontWeight: 800,
              color: COR.tinta,
              letterSpacing: '-.02em',
              textWrap: 'balance',
            }}
          >
            {comGestao ? (
              <>
                Gestão de saúde e<br />
                segurança do trabalho
              </>
            ) : (
              <>
                Saúde mental e<br />
                riscos psicossociais
              </>
            )}
          </Box>
          <Box component="p" sx={{ mt: 2, maxWidth: 620, fontSize: 15 }}>
            {comGestao
              ? 'Uma plataforma onde a conformidade de SST vive inteira e avisa sozinha antes de virar autuação.'
              : 'O que a NR-1 passou a exigir no inventário de riscos, medido com instrumento validado e acompanhado ao longo do ano.'}
          </Box>
        </Box>

        {/* --- a quem se destina --- */}
        <Box
          className="bloco"
          sx={{
            mt: 4,
            bgcolor: COR.superficie,
            borderRadius: 2,
            p: 3,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: 2.5,
          }}
        >
          <Campo rotulo="Cliente" valor={proposta.clientName} />
          <Campo rotulo="CNPJ" valor={cnpjBR(proposta.clientCnpj)} />
          <Campo rotulo="Vidas" valor={String(t.vidas)} />
        </Box>

        {/* --- o que entra: a lista completa só quando a gestão está na proposta --- */}
        {comGestao && (
        <Secao numero={proximo()} titulo="O que a sua empresa passa a ter">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            {O_QUE_PASSA_A_TER.map((cartao) => (
              <Box
                key={cartao.titulo}
                className="bloco"
                sx={{ border: `1px solid ${COR.borda}`, borderRadius: 2, p: 3 }}
              >
                <Box component="h3" sx={{ fontSize: 16, fontWeight: 700, color: COR.tinta, mb: 1.5 }}>
                  {cartao.titulo}
                </Box>
                <Box component="ul" sx={{ fontSize: 13.5 }}>
                  {cartao.itens.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </Secao>
        )}

        {/* --- os avisos: só fazem sentido com a gestão --- */}
        {comGestao && (
        <Secao numero={proximo()} titulo="Por que os avisos mudam o jogo">
          <Box component="p">
            É a parte que parece pequena na proposta e é a que mais evita multa. Quem controla
            vencimento em planilha descobre o atraso quando o fiscal pergunta.
          </Box>
          <Box component="p" sx={{ mt: 2 }}>
            O sistema varre oito frentes todos os dias e avisa antes do prazo: exame, treinamento,
            EPI, EPC, equipamento de medição, certificado digital, CNH de motorista e procuração
            eletrônica.
          </Box>
          <Box
            className="bloco"
            sx={{ mt: 3, bgcolor: COR.navy, color: '#E8EEF6', borderRadius: 2, p: 3, fontSize: 14 }}
          >
            Quando um exame está para vencer, o sistema não só avisa: já dispara a convocação do
            trabalhador por WhatsApp ou e-mail, com data, horário e endereço da clínica.{' '}
            <Box component="strong" sx={{ color: COR.ciano }}>
              O RH deixa de ser a agenda de todo mundo.
            </Box>
          </Box>
        </Secao>
        )}

        {/* --- Clube, só quando entra na proposta --- */}
        {proposta.clubEnabled && (
          <Secao numero={proximo()} titulo="Clube Lidera">
            <Box component="p">
              {comGestao
                ? 'Um complemento de bem-estar, contratado à parte. Enquanto a gestão cuida da conformidade, o Clube cuida da saúde mental, que a NR-1 passou a exigir no inventário de riscos e que nenhuma planilha mede.'
                : 'O acompanhamento de saúde mental do colaborador, que a NR-1 passou a exigir no inventário de riscos e que nenhuma planilha mede.'}
            </Box>
            <Box
              className="bloco"
              sx={{
                mt: 3,
                border: `1px solid ${COR.borda}`,
                borderRadius: 2,
                p: 3,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '190px 1fr' },
                gap: 3,
                alignItems: 'center',
              }}
            >
              <Box sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                <Box sx={{ fontSize: 26, fontWeight: 800, color: COR.navy, letterSpacing: '-.02em' }}>
                  Clube<Box component="span" sx={{ color: COR.destaque }}>Lidera</Box>
                </Box>
                <Box
                  sx={{
                    fontSize: 9.5,
                    fontWeight: 600,
                    letterSpacing: '.18em',
                    color: COR.suave,
                    textTransform: 'uppercase',
                  }}
                >
                  Clube de benefícios
                </Box>
              </Box>
              <Box component="ul" sx={{ fontSize: 13.5 }}>
                {DO_CLUBE.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </Box>
            </Box>
            {comGestao && (
              <Box component="p" sx={{ mt: 2, fontSize: 13, color: COR.suave }}>
                É um complemento opcional. Funciona sobre a gestão e pode ser contratado depois,
                quando a operação estiver rodando.
              </Box>
            )}
          </Secao>
        )}

        {/* --- os módulos que a negociação incluiu --- */}
        {modulos.length > 0 && (
          <Secao numero={proximo()} titulo="Módulos inclusos">
            <Box component="p" sx={{ mb: 2 }}>
              Entram junto com o que foi contratado, sem custo adicional ao valor abaixo.
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2.5,
              }}
            >
              {modulos.map((m) => (
                <Box
                  key={m.name}
                  className="bloco"
                  sx={{ border: `1px solid ${COR.borda}`, borderRadius: 2, p: 3 }}
                >
                  <Box
                    component="h3"
                    sx={{ fontSize: 15.5, fontWeight: 700, color: COR.tinta, mb: m.description ? 1 : 0 }}
                  >
                    {m.name}
                  </Box>
                  {m.description && (
                    <Box component="p" sx={{ fontSize: 13.5 }}>
                      {m.description}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Secao>
        )}

        {/* --- investimento --- */}
        <Secao numero={proximo()} titulo="Investimento">
          <Box component="p">
            {comGestao && proposta.clubEnabled
              ? 'Duas linhas independentes. A gestão é por vida; o Clube é por faixa.'
              : comGestao
                ? 'Uma linha só: a gestão é cobrada por vida ativa no período.'
                : 'O Clube Lidera é cobrado por faixa, com o excedente por vida acima dela.'}
          </Box>

          <Box
            sx={{
              mt: 3,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: comGestao && proposta.clubEnabled ? '1fr 1fr' : '1fr',
              },
              gap: 2.5,
            }}
          >
            {comGestao && (
            <Box
              className="bloco"
              sx={{ bgcolor: COR.navy, color: '#fff', borderRadius: 2, p: 3.5 }}
            >
              <Box
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '.2em',
                  textTransform: 'uppercase',
                  color: '#9FB6CE',
                }}
              >
                Gestão de SST
              </Box>
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Box sx={{ fontSize: 38, fontWeight: 800, letterSpacing: '-.02em', lineHeight: 1 }}>
                  {emReais(t.gestao)}
                </Box>
                <Box sx={{ fontSize: 14, color: '#9FB6CE' }}>/ mês</Box>
              </Box>
              <Box sx={{ mt: 1.5, fontSize: 13, color: '#C6D5E4' }}>
                {proposta.showPricePerLife
                  ? `${t.vidas} vidas × ${emReais(t.valorPorVida)} por vida. Cobre todos os itens da seção 01, sem taxa de implantação.`
                  : 'Cobre todos os itens da seção 01. Sem taxa de implantação.'}
              </Box>
            </Box>
            )}

            {proposta.clubEnabled && (
              <Box
                className="bloco"
                sx={{ border: `2px solid ${COR.ciano}`, borderRadius: 2, p: 3.5 }}
              >
                <Box
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    letterSpacing: '.2em',
                    textTransform: 'uppercase',
                    color: COR.destaque,
                  }}
                >
                  {comGestao ? 'Clube Lidera (opcional)' : 'Clube Lidera'}
                </Box>
                <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                  <Box
                    sx={{
                      fontSize: 38,
                      fontWeight: 800,
                      letterSpacing: '-.02em',
                      lineHeight: 1,
                      color: COR.tinta,
                    }}
                  >
                    {emReais(t.clube)}
                  </Box>
                  <Box sx={{ fontSize: 13, color: COR.suave }}>
                    por mês, para {t.vidas} {t.vidas === 1 ? 'vida' : 'vidas'}
                  </Box>
                </Box>
                <Box sx={{ mt: 1.5, fontSize: 13 }}>
                  Valor fechado até {proposta.clubBaseLives} vidas. Acima disso,{' '}
                  {emReais(proposta.clubExtraPerLife)} por vida excedente.
                </Box>
              </Box>
            )}
          </Box>

          <Box className="bloco" sx={{ mt: 4 }}>
            <Box component="h3" sx={{ fontSize: 16, fontWeight: 700, color: COR.tinta, mb: 1.5 }}>
              {`O seu caso — ${t.vidas} ${t.vidas === 1 ? 'vida' : 'vidas'}`}
            </Box>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <Box component="thead">
                <Box component="tr">
                  <Celula cabecalho>Item</Celula>
                  <Celula cabecalho>Cálculo</Celula>
                  <Celula cabecalho alinhado="right">
                    Mensal
                  </Celula>
                </Box>
              </Box>
              <Box component="tbody">
                {comGestao && (
                <Box component="tr">
                  <Celula>Gestão de SST</Celula>
                  <Celula suave>
                    {proposta.showPricePerLife
                      ? `${t.vidas} × ${emReais(t.valorPorVida)}`
                      : `${t.vidas} vidas`}
                  </Celula>
                  <Celula alinhado="right">{emReais(t.gestao)}</Celula>
                </Box>
                )}

                {proposta.clubEnabled && (
                  <Box component="tr">
                    <Celula>Clube Lidera, faixa</Celula>
                    <Celula suave>{`faixa fechada até ${proposta.clubBaseLives} vidas`}</Celula>
                    <Celula alinhado="right">{emReais(t.clubeBase)}</Celula>
                  </Box>
                )}

                {proposta.clubEnabled && t.vidasExcedentes > 0 && (
                  <Box component="tr">
                    <Celula>Clube Lidera, excedente</Celula>
                    <Celula suave>
                      {`${t.vidasExcedentes} × ${emReais(proposta.clubExtraPerLife)}`}
                    </Celula>
                    <Celula alinhado="right">{emReais(t.clubeExcedente)}</Celula>
                  </Box>
                )}

                <Box component="tr" sx={{ bgcolor: COR.totalFundo }}>
                  <Celula forte>Total mensal</Celula>
                  <Celula />
                  <Celula forte alinhado="right" cor={COR.destaque}>
                    {emReais(t.total)}
                  </Celula>
                </Box>
              </Box>
            </Box>
            <Box component="p" sx={{ mt: 2, fontSize: 12.5, color: COR.suave }}>
              Contrato de {proposta.contractMonths} meses — {emReais(t.totalDoContrato)} no período.
              O valor mensal acompanha o número real de vidas ativas.
            </Box>
          </Box>
        </Secao>

        {/* --- exclusão: só faz sentido quando há plano de gestão a excluir do ASO --- */}
        {comGestao && !proposta.includesAso && (
          <Secao numero={proximo()} titulo="O que não está incluído">
            <Box className="bloco" sx={{ borderLeft: `4px solid ${COR.destaque}`, pl: 3 }}>
              <Box component="h3" sx={{ fontSize: 15.5, fontWeight: 700, color: COR.tinta, mb: 1 }}>
                Exames e ASO
              </Box>
              <Box component="p">
                A realização dos exames ocupacionais e a emissão do ASO não fazem parte do plano de
                gestão. Eles são executados pela clínica de medicina ocupacional e cobrados por
                atendimento, conforme a tabela dela.
              </Box>
              <Box component="p" sx={{ mt: 2 }}>
                O plano cobre toda a gestão em volta: agendar, convocar, controlar prazo, guardar o
                resultado, montar o PCMSO e transmitir o evento ao eSocial. O exame em si é ato
                médico e tem custo próprio.
              </Box>
            </Box>
          </Secao>
        )}

        {/* --- prazo e condições --- */}
        <Secao numero={proximo()} titulo="Prazo e condições">
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <Box component="tbody">
              <Condicao rotulo="Vigência mínima" valor={`${proposta.contractMonths} meses`} />
              <Condicao
                rotulo="Cobrança"
                valor={
                  comGestao
                    ? 'Mensal, sobre o número de vidas ativas no período'
                    : 'Mensal, por faixa de vidas, com o excedente por vida acima dela'
                }
              />
              <Condicao
                rotulo="Implantação"
                valor={
                  comGestao
                    ? 'Sem custo. Inclui importação do cadastro de funcionários e treinamento das equipes'
                    : 'Sem custo. Inclui a configuração das campanhas e o convite aos colaboradores'
                }
              />
              {comGestao && (
                <Condicao
                  rotulo="Exames e ASO"
                  valor={
                    proposta.includesAso
                      ? 'Inclusos nos valores acima, conforme a rede credenciada'
                      : 'Cobrados à parte pela clínica executante, por atendimento'
                  }
                />
              )}
              <Condicao
                rotulo="Reajuste"
                valor={proposta.readjustmentNote ?? 'Anual, pelo IPCA'}
              />
              <Condicao rotulo="Validade da proposta" valor={dataBR(proposta.validUntil)} />
            </Box>
          </Box>
        </Secao>

        {/* --- por que confiar --- */}
        <Secao numero={proximo()} titulo="Por que confiar">
          <Box component="p" sx={{ mb: 3 }}>
            Cada item aqui é verificável, e é por isso que está na proposta.
          </Box>
          <Box
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}
          >
            {(comGestao ? POR_QUE_CONFIAR : POR_QUE_CONFIAR_CLUBE).map((cartao) => (
              <Box
                key={cartao.titulo}
                className="bloco"
                sx={{ bgcolor: COR.superficie, borderRadius: 2, p: 3 }}
              >
                <Box component="h3" sx={{ fontSize: 15.5, fontWeight: 700, color: COR.tinta, mb: 1 }}>
                  {cartao.titulo}
                </Box>
                <Box component="p" sx={{ fontSize: 13.5 }}>
                  {cartao.texto}
                </Box>
              </Box>
            ))}
          </Box>
        </Secao>

        {/* --- como começa --- */}
        <Secao numero={proximo()} titulo="Como começa">
          <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {(comGestao ? COMO_COMECA : COMO_COMECA_CLUBE).map((passo, indice) => (
              <Box
                key={passo}
                component="li"
                sx={{ display: 'flex', gap: 2.5, mb: 1.5, alignItems: 'baseline' }}
              >
                <Box sx={{ color: COR.destaque, fontWeight: 800, minWidth: 16 }}>{indice + 1}</Box>
                <Box>{passo}</Box>
              </Box>
            ))}
          </Box>
        </Secao>

        {/* --- próximo passo --- */}
        <Box
          className="bloco"
          sx={{
            mt: 6,
            borderRadius: 2,
            p: { xs: 3, md: 5 },
            background: `linear-gradient(120deg, ${COR.navy} 0%, ${COR.navyClaro} 100%)`,
            color: '#fff',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr' },
            gap: { xs: 3, sm: 5 },
          }}
        >
          <Box>
            <Box
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '.2em',
                textTransform: 'uppercase',
                color: '#9FB6CE',
              }}
            >
              Próximo passo
            </Box>
            <Box
              component="h2"
              sx={{ mt: 1.5, fontSize: 24, fontWeight: 800, lineHeight: 1.25, letterSpacing: '-.01em' }}
            >
              Uma demonstração de trinta minutos com os dados da sua empresa
            </Box>
            <Box component="p" sx={{ mt: 2, fontSize: 13.5, color: '#C6D5E4' }}>
              {comGestao
                ? 'Você vê a convocação sair e o documento ser emitido a partir do seu próprio cadastro, não de um exemplo genérico.'
                : 'Você vê a campanha sendo montada, o convite chegando ao colaborador e o resultado lido por setor — com os seus números, não com um exemplo.'}
            </Box>
          </Box>

          <Box sx={{ borderLeft: { sm: '1px solid rgba(255,255,255,.22)' }, pl: { sm: 4 } }}>
            <Box sx={{ fontSize: 17, fontWeight: 700 }}>
              {proposta.consultantName ?? aCasa}
            </Box>
            {/* "Consultor comercial" só quando há uma pessoa: sem nome sobra o da clínica, e
                rotular a clínica de consultor é dizer algo que não é verdade. */}
            <Box sx={{ fontSize: 13.5, color: '#C6D5E4', mb: 2 }}>
              {proposta.consultantName ? 'Consultor comercial' : 'Atendimento comercial'}
            </Box>
            {proposta.consultantPhone && (
              <Box sx={{ fontSize: 13.5, color: COR.ciano }}>{proposta.consultantPhone}</Box>
            )}
            {proposta.consultantEmail && (
              <Box sx={{ fontSize: 13.5, color: COR.ciano }}>{proposta.consultantEmail}</Box>
            )}
            <Box sx={{ fontSize: 13.5, color: COR.ciano }}>app.liderasst.com.br</Box>
          </Box>
        </Box>

        {/* Rodapé visível na tela; na impressão o fixo abaixo assume, em toda página. */}
        <Box
          sx={{
            mt: 5,
            pt: 2,
            borderTop: `1px solid ${COR.borda}`,
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 11.5,
            color: COR.suave,
          }}
        >
          <span>
            {aCasa} · Proposta comercial {proposta.number}
          </span>
          <span>app.liderasst.com.br</span>
        </Box>
      </Box>
    </>
  );
};

/** O chassi cinza em volta da folha. Vive aqui para as duas telas partirem do mesmo lugar. */
export const FolhaEmBranco: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ bgcolor: '#EDF1F5', minHeight: '100vh', pb: 6, fontFamily: FAMILIA }}>{children}</Box>
);


/**
 * A marca no cabeçalho: o escudo do logo do sistema, mais o nome em tipografia.
 *
 * O arquivo configurado (`VITE_LOGO_*`) traz o escudo **e** a palavra "LIDERA SST" em branco —
 * feito para a barra escura do topo do sistema. Sobre o papel branco do documento, a palavra
 * sumiria e sobraria um escudo solto. Então o escudo vem da imagem, recortado, e o nome é
 * composto ao lado — que é exatamente o arranjo da proposta do Design.
 *
 * O recorte foi medido no arquivo que o sistema serve hoje (718×268; o escudo ocupa x 16–175,
 * y 39–233) e conferido contra o que está no ar em app.liderasst.com.br: mesmo arquivo, mesmo
 * sha256. Trocar o logo por um de outro enquadramento pede remedir estes quatro números.
 */
const ESCUDO = { largura: 718, altura: 268, x: 16, y: 39, w: 159, h: 194 } as const;

const Marca: React.FC = () => {
  const alturaDesejada = 46;
  const escala = alturaDesejada / ESCUDO.h;

  const partes = branding.appName.trim().split(' ');
  const ultima = partes.length > 1 ? partes.pop() : undefined;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box
        aria-hidden
        sx={{
          width: ESCUDO.w * escala,
          height: alturaDesejada,
          flexShrink: 0,
          backgroundImage: `url(${branding.logoLight})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: `${ESCUDO.largura * escala}px ${ESCUDO.altura * escala}px`,
          backgroundPosition: `-${ESCUDO.x * escala}px -${ESCUDO.y * escala}px`,
        }}
      />
      <Box sx={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.01em', color: COR.navy }}>
        {partes.join(' ')}
        {ultima && (
          <Box component="span" sx={{ color: COR.destaque }}>
            {` ${ultima}`}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const DadoDoTopo: React.FC<{ rotulo: string; valor: string }> = ({ rotulo, valor }) => (
  <Box>
    <Box component="span" sx={{ fontWeight: 700, color: COR.tinta }}>
      {rotulo}{' '}
    </Box>
    <Box component="span" sx={{ color: COR.suave }}>
      {valor}
    </Box>
  </Box>
);

const Campo: React.FC<{ rotulo: string; valor: string }> = ({ rotulo, valor }) => (
  <Box>
    <Box
      sx={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '.2em',
        textTransform: 'uppercase',
        color: COR.suave,
        mb: 0.75,
      }}
    >
      {rotulo}
    </Box>
    <Box sx={{ fontSize: 15, fontWeight: 600, color: COR.tinta }}>{valor}</Box>
  </Box>
);

const Secao: React.FC<{ numero: string; titulo: string; children: React.ReactNode }> = ({
  numero,
  titulo,
  children,
}) => (
  <Box sx={{ mt: 6 }}>
    <Box className="bloco" sx={{ display: 'flex', alignItems: 'baseline', gap: 2.5, mb: 2.5 }}>
      <Box
        sx={{
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '.16em',
          color: COR.destaque,
          minWidth: 26,
        }}
      >
        {numero}
      </Box>
      <Box component="h2" sx={{ fontSize: 24, fontWeight: 800, color: COR.tinta, letterSpacing: '-.01em' }}>
        {titulo}
      </Box>
    </Box>
    {children}
  </Box>
);

const Celula: React.FC<{
  children?: React.ReactNode;
  cabecalho?: boolean;
  forte?: boolean;
  suave?: boolean;
  alinhado?: 'left' | 'right';
  cor?: string;
}> = ({ children, cabecalho, forte, suave, alinhado = 'left', cor }) => (
  <Box
    component={cabecalho ? 'th' : 'td'}
    sx={{
      textAlign: alinhado,
      padding: '10px 12px',
      borderBottom: `1px solid ${COR.borda}`,
      fontSize: cabecalho ? 12 : 13.5,
      fontWeight: cabecalho || forte ? 700 : 400,
      color: cor ?? (cabecalho || suave ? COR.suave : COR.texto),
      fontVariantNumeric: 'tabular-nums',
      ...(cabecalho
        ? { textTransform: 'none' as const, bgcolor: COR.superficie }
        : {}),
    }}
  >
    {children}
  </Box>
);

const Condicao: React.FC<{ rotulo: string; valor: string }> = ({ rotulo, valor }) => (
  <Box component="tr">
    <Box
      component="td"
      sx={{
        padding: '11px 12px 11px 0',
        borderBottom: `1px solid ${COR.borda}`,
        fontWeight: 700,
        color: COR.tinta,
        width: '30%',
        verticalAlign: 'top',
      }}
    >
      {rotulo}
    </Box>
    <Box
      component="td"
      sx={{ padding: '11px 0', borderBottom: `1px solid ${COR.borda}`, color: COR.texto }}
    >
      {valor}
    </Box>
  </Box>
);

export default DocumentoDaProposta;
